import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { Header, Footer } from "../components/Layout";
import { Scale } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("citizen");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") {
        const r = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(r.data.user);
      } else {
        const r = await api.post("/auth/register", { email: form.email, password: form.password, name: form.name, role });
        setUser(r.data.user);
      }
      navigate("/dashboard");
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-md mx-auto px-4 pt-12 pb-24">
        <div className="j-card fade-up" data-testid="login-card">
          <div className="w-12 h-12 rounded-xl bg-[#1E293B] flex items-center justify-center mb-4">
            <Scale className="w-6 h-6 text-[#10B981]" />
          </div>
          <h1 className="font-heading font-black text-2xl text-[#1E293B]">
            {mode === "login" ? "Connexion" : "Créer un compte"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {mode === "login" ? "Accédez à votre dashboard JURISIA." : "Rejoignez la permanence d'urgence."}
          </p>

          <button onClick={google} className="w-full mt-6 border border-slate-300 rounded-full h-11 flex items-center justify-center gap-2 bg-white hover:bg-slate-50 font-semibold text-slate-700" data-testid="google-login-btn">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continuer avec Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase text-slate-400 tracking-widest">ou</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <>
                <input className="j-input" placeholder="Nom complet" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" />
                <div className="flex gap-2">
                  {["citizen", "lawyer"].map((r) => (
                    <button key={r} type="button" onClick={() => setRole(r)}
                      className={`flex-1 h-10 rounded-full text-sm font-semibold border ${role === r ? "border-[#10B981] bg-[#10B981]/10 text-[#065F46]" : "border-slate-200 text-slate-600"}`}
                      data-testid={`role-${r}-btn`}>
                      {r === "citizen" ? "Particulier" : "Avocat"}
                    </button>
                  ))}
                </div>
              </>
            )}
            <input type="email" className="j-input" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" />
            <input type="password" className="j-input" placeholder="Mot de passe" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" />
            {err && <div className="text-sm text-red-600" data-testid="auth-error">{err}</div>}
            <button type="submit" disabled={loading} className="btn-emerald w-full" data-testid="auth-submit-btn">
              {loading ? "..." : mode === "login" ? "Connexion" : "Créer mon compte"}
            </button>
          </form>

          <div className="text-center text-sm text-slate-600 mt-5">
            {mode === "login" ? (
              <>Pas de compte ? <button onClick={() => setMode("signup")} className="text-[#10B981] font-semibold" data-testid="switch-signup">Créer un compte</button></>
            ) : (
              <>Déjà inscrit ? <button onClick={() => setMode("login")} className="text-[#10B981] font-semibold" data-testid="switch-login">Se connecter</button></>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
